import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Col, Modal, Row } from 'react-bootstrap';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import FilterPanel from '@spark/ui-components/components/FilterPanel';
import { notification } from '@spark/ui-components/utils/notification';
import {
  ASSOCIACAO_PREMIACAO_MAX_RECORDS,
  createPremiacaoAssociacaoMatriz
} from '@spark/services/premiacao';
import { usePremiacaoAssociacaoData } from '../hooks/usePremiacaoAssociacaoData';
import { filtrarTabelasAssociacao, filtrarVeiculosAssociacao } from '../utils/associacaoFilters';
import { getAssociacaoVeiculoColumns } from '../config/associacaoVeiculoColumns';
import { getAssociacaoTabelaColumns } from '../config/associacaoTabelaColumns';
import {
  applyOptionsToFilters,
  buildSelectOptionsFromField
} from '../utils/premiacaoMultiSelectFilters';
import { associacaoVeiculoFilterConfig } from '../config/associacaoVeiculoFilterConfig';
import { associacaoTabelaFilterConfig } from '../config/associacaoTabelaFilterConfig';
import PremiacaoVeiculoGerenciarModal from './PremiacaoVeiculoGerenciarModal';
import PremiacaoTabelaGerenciarModal from './PremiacaoTabelaGerenciarModal';
import { buildVeiculosLookup } from '../utils/enriquecerVeiculosVinculo';

const PremiacaoAssociacaoModal = ({ show, onHide }) => {
  const [filtrosVeiculo, setFiltrosVeiculo] = useState({});
  const [filtrosTabela, setFiltrosTabela] = useState({});
  const [selectedVeiculos, setSelectedVeiculos] = useState([]);
  const [selectedTabelas, setSelectedTabelas] = useState([]);
  const [vinculando, setVinculando] = useState(false);
  const [veiculoGerenciar, setVeiculoGerenciar] = useState(null);
  const [showVeiculoGerenciar, setShowVeiculoGerenciar] = useState(false);
  const [tabelaGerenciar, setTabelaGerenciar] = useState(null);
  const [showTabelaGerenciar, setShowTabelaGerenciar] = useState(false);

  const {
    veiculos,
    tabelas,
    loading,
    veiculosTruncados,
    tabelasTruncadas,
    reload
  } = usePremiacaoAssociacaoData(show);

  useEffect(() => {
    if (!show) {
      setFiltrosVeiculo({});
      setFiltrosTabela({});
      setSelectedVeiculos([]);
      setSelectedTabelas([]);
      setVeiculoGerenciar(null);
      setShowVeiculoGerenciar(false);
      setTabelaGerenciar(null);
      setShowTabelaGerenciar(false);
    }
  }, [show]);

  const veiculosFiltrados = useMemo(
    () => filtrarVeiculosAssociacao(veiculos, filtrosVeiculo),
    [veiculos, filtrosVeiculo]
  );

  const tabelasFiltradas = useMemo(
    () => filtrarTabelasAssociacao(tabelas, filtrosTabela),
    [tabelas, filtrosTabela]
  );

  const veiculosLookup = useMemo(
    () => buildVeiculosLookup(veiculos),
    [veiculos]
  );

  const veiculoFilterConfig = useMemo(
    () => applyOptionsToFilters(associacaoVeiculoFilterConfig, {
      Segmento: buildSelectOptionsFromField(veiculos, 'segmento'),
      Modelo: buildSelectOptionsFromField(veiculos, 'modelo')
    }),
    [veiculos]
  );

  const tabelaFilterConfig = useMemo(
    () => applyOptionsToFilters(associacaoTabelaFilterConfig, {
      Segmento: buildSelectOptionsFromField(tabelas, 'segmento')
    }),
    [tabelas]
  );

  const totalParesVinculo = useMemo(
    () => selectedVeiculos.length * selectedTabelas.length,
    [selectedVeiculos.length, selectedTabelas.length]
  );

  const selectedVeiculoIds = useMemo(
    () => selectedVeiculos.map((item) => item.id),
    [selectedVeiculos]
  );

  const selectedTabelaIds = useMemo(
    () => selectedTabelas.map((item) => String(item.id)),
    [selectedTabelas]
  );

  const allVeiculosPageSelected = useMemo(
    () => veiculosFiltrados.length > 0
      && veiculosFiltrados.every((item) => selectedVeiculoIds.includes(item.id)),
    [veiculosFiltrados, selectedVeiculoIds]
  );

  const allTabelasPageSelected = useMemo(
    () => tabelasFiltradas.length > 0
      && tabelasFiltradas.every((item) => selectedTabelaIds.includes(String(item.id))),
    [tabelasFiltradas, selectedTabelaIds]
  );

  const handleToggleVeiculo = useCallback((rowId) => {
    setSelectedVeiculos((prev) => {
      const exists = prev.some((item) => item.id === rowId);
      if (exists) {
        return prev.filter((item) => item.id !== rowId);
      }
      const row = veiculosFiltrados.find((item) => item.id === rowId);
      return row ? [...prev, row] : prev;
    });
  }, [veiculosFiltrados]);

  const handleToggleTabela = useCallback((rowId) => {
    setSelectedTabelas((prev) => {
      const exists = prev.some((item) => String(item.id) === rowId);
      if (exists) {
        return prev.filter((item) => String(item.id) !== rowId);
      }
      const row = tabelasFiltradas.find((item) => String(item.id) === rowId);
      return row ? [...prev, row] : prev;
    });
  }, [tabelasFiltradas]);

  const handleToggleSelectAllVeiculos = useCallback(() => {
    setSelectedVeiculos((prev) => {
      if (allVeiculosPageSelected) {
        const pageIds = new Set(veiculosFiltrados.map((item) => item.id));
        return prev.filter((item) => !pageIds.has(item.id));
      }
      const merged = new Map(prev.map((item) => [item.id, item]));
      veiculosFiltrados.forEach((item) => merged.set(item.id, item));
      return Array.from(merged.values());
    });
  }, [allVeiculosPageSelected, veiculosFiltrados]);

  const handleToggleSelectAllTabelas = useCallback(() => {
    setSelectedTabelas((prev) => {
      if (allTabelasPageSelected) {
        const pageIds = new Set(tabelasFiltradas.map((item) => String(item.id)));
        return prev.filter((item) => !pageIds.has(String(item.id)));
      }
      const merged = new Map(prev.map((item) => [String(item.id), item]));
      tabelasFiltradas.forEach((item) => merged.set(String(item.id), item));
      return Array.from(merged.values());
    });
  }, [allTabelasPageSelected, tabelasFiltradas]);

  const executarVinculo = useCallback(async () => {
    if (selectedVeiculos.length === 0 || selectedTabelas.length === 0 || vinculando) {
      return;
    }

    try {
      setVinculando(true);
      const result = await createPremiacaoAssociacaoMatriz({
        NumerosFrota: selectedVeiculos.map((item) => item.numeroFrota),
        IdsTabelaPremiacao: selectedTabelas.map((item) => item.id)
      });
      const data = result?.data || result || {};
      const inseridos = data.inseridos ?? data.Inseridos ?? 0;
      const atualizados = data.atualizados ?? data.Atualizados ?? 0;
      const total = inseridos + atualizados;

      notification.success(
        total > 0
          ? `${total} vínculo(s) processado(s) com sucesso!`
          : 'Nenhum vínculo novo foi criado.'
      );

      setSelectedVeiculos([]);
      setSelectedTabelas([]);
      reload();
    } catch (error) {
      const mensagem = error?.response?.data?.error || error?.message || 'Erro ao vincular';
      notification.error(mensagem, 'Erro ao vincular');
      console.error('Erro ao vincular associacao matriz:', error);
    } finally {
      setVinculando(false);
    }
  }, [reload, selectedTabelas, selectedVeiculos, vinculando]);

  const handleVincular = useCallback(() => {
    if (selectedVeiculos.length === 0 || selectedTabelas.length === 0) {
      notification.warning('Selecione ao menos uma frota e uma tabela.');
      return;
    }

    if (totalParesVinculo > 1) {
      const confirmar = window.confirm(
        `Serão processados ${totalParesVinculo} vínculo(s) `
        + `(${selectedVeiculos.length} frota(s) x ${selectedTabelas.length} tabela(s)). `
        + 'Pares já existentes serão ignorados. Deseja continuar?'
      );
      if (!confirmar) {
        return;
      }
    }

    executarVinculo();
  }, [executarVinculo, selectedTabelas.length, selectedVeiculos.length, totalParesVinculo]);

  const veiculoColumns = useMemo(
    () => getAssociacaoVeiculoColumns({
      selectedIds: selectedVeiculoIds,
      onToggleSelect: handleToggleVeiculo,
      onToggleSelectAll: handleToggleSelectAllVeiculos,
      allPageSelected: allVeiculosPageSelected,
      selectAllDisabled: veiculosFiltrados.length === 0,
      onGerenciar: (veiculo) => {
        setVeiculoGerenciar(veiculo);
        setShowVeiculoGerenciar(true);
      }
    }),
    [
      selectedVeiculoIds,
      handleToggleVeiculo,
      handleToggleSelectAllVeiculos,
      allVeiculosPageSelected,
      veiculosFiltrados.length
    ]
  );

  const tabelaColumns = useMemo(
    () => getAssociacaoTabelaColumns({
      selectedIds: selectedTabelaIds,
      onToggleSelect: handleToggleTabela,
      onToggleSelectAll: handleToggleSelectAllTabelas,
      allPageSelected: allTabelasPageSelected,
      selectAllDisabled: tabelasFiltradas.length === 0,
      onGerenciar: (tabela) => {
        setTabelaGerenciar(tabela);
        setShowTabelaGerenciar(true);
      }
    }),
    [
      selectedTabelaIds,
      handleToggleTabela,
      handleToggleSelectAllTabelas,
      allTabelasPageSelected,
      tabelasFiltradas.length
    ]
  );

  return (
    <>
      <Modal show={show} onHide={onHide} animation={false} size="xl" dialogClassName="modal-uniform-xl">
        <Modal.Header closeButton>
          <Modal.Title>Associação Tabela × Frota</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(veiculosTruncados || tabelasTruncadas) ? (
            <Alert variant="warning" className="mb-3">
              {`A listagem foi limitada a ${ASSOCIACAO_PREMIACAO_MAX_RECORDS} registros por entidade. Refine a busca ou use filtros adicionais.`}
            </Alert>
          ) : null}

          <Row>
            <Col lg={6} className="mb-3">
              <FilterPanel
                filters={veiculoFilterConfig}
                onApply={setFiltrosVeiculo}
                title="Filtros de Veículos"
                loading={loading}
                defaultCollapsed={false}
              />
              <TanStackTable
                columns={veiculoColumns}
                data={veiculosFiltrados}
                loading={loading}
                withPagination={false}
                enableRowColors={true}
                enableCopy={true}
                className="mt-2"
              />
            </Col>

            <Col lg={6} className="mb-3">
              <FilterPanel
                filters={tabelaFilterConfig}
                onApply={setFiltrosTabela}
                title="Filtros de Tabelas"
                loading={loading}
                defaultCollapsed={false}
              />
              <TanStackTable
                columns={tabelaColumns}
                data={tabelasFiltradas}
                loading={loading}
                withPagination={false}
                enableRowColors={true}
                enableCopy={true}
                className="mt-2"
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <div className="text-muted">
              {selectedVeiculos.length} frota(s) · {selectedTabelas.length} tabela(s) selecionada(s)
            </div>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={onHide} disabled={vinculando}>
                Fechar
              </Button>
              <ButtonWithLoading
                variant="primary"
                type="button"
                onClick={handleVincular}
                loading={vinculando}
                disabled={selectedVeiculos.length === 0 || selectedTabelas.length === 0}
              >
                Vincular selecionados
              </ButtonWithLoading>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <PremiacaoVeiculoGerenciarModal
        show={showVeiculoGerenciar}
        onHide={() => setShowVeiculoGerenciar(false)}
        veiculo={veiculoGerenciar}
        onSuccess={reload}
      />

      <PremiacaoTabelaGerenciarModal
        show={showTabelaGerenciar}
        onHide={() => setShowTabelaGerenciar(false)}
        tabelaId={tabelaGerenciar?.id}
        veiculosLookup={veiculosLookup}
        onSuccess={reload}
      />
    </>
  );
};

export default PremiacaoAssociacaoModal;
